import React, { FC, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  List,
  ListItem,
  TextField,
  Typography,
} from '@mui/material';
import { useUnit } from 'effector-react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { TitleItem } from '../TitleItem';
import { setMenuView } from '../model';
import { $accountGetListFXPending, $accountsForAccess, $currentAccount, accountGetListFX } from './model';
import { Account, PUpdate } from '../../../../apiRPC/board';
import { Members } from '../../../pages/desk/modals/TaskOverview/Members';
import { MenuMembers } from '../../../pages/desk/Card/Members/MenuMembers';
import { $board, updateBoardFX } from '../../../model/board';
import { $account } from '../../../../model/account';

export type Field = {
  hasRestrictedAccess: boolean;
  allowedAccounts?: Account[];
  cardsHaveCyrillicTitles: boolean;
};
export const Acces:FC = () => {
 const [accountsForAces,accounts,board,accountGetListFXPending,account]=useUnit([$accountsForAccess,$currentAccount,$board,$accountGetListFXPending,$account])

  const formik = useFormik<Field>({
    initialValues: {
      hasRestrictedAccess: false,
      allowedAccounts: undefined,
      cardsHaveCyrillicTitles: true,
    },
    validationSchema: Yup.object({
      hasRestrictedAccess: Yup.boolean(),
      cardsHaveCyrillicTitles: Yup.boolean(),
      allowedAccounts: Yup.array()
        .of(Yup.object().shape({
          uuid: Yup.string().required(),
          email: Yup.string().required(),
        }))
        .when('hasRestrictedAccess', {
          is: true,
          then: (schema) => schema.required('Должен быть выбран хотя бы один аккаунт').min(1, 'Должен быть выбран хотя бы один аккаунт'),
          otherwise: (schema) => schema,
        }),
    }),
    validateOnBlur: false,
    validateOnChange: true,
    validateOnMount: false,
    onSubmit: (values, f) => {
      if (!board) return;
      setMenuView('Default')
      f.resetForm();

      const allowedAccounts = values.allowedAccounts?.map((item) => item.uuid) ?? [];
      allowedAccounts.push(account?.uuid ?? '');
      const allowedAccountsNotOwner = allowedAccounts.filter(
        (item) => item !== board.owner,
      );
      const data: PUpdate = {
        uuid: board.uuid,
        fields: {
          hasRestrictedAccess: values.hasRestrictedAccess,
          allowedAccounts: values.hasRestrictedAccess ? allowedAccountsNotOwner : undefined,
          cardsHaveCyrillicTitles: values.cardsHaveCyrillicTitles,
        },
      };
      updateBoardFX(data);
    },
  });

  useEffect(() => {
    if(!accounts.length)return
    formik.setFieldValue('allowedAccounts', accounts,true);
  }, [accounts]);

  useEffect(() => {
    if (!board) return;
    formik.setFieldValue('hasRestrictedAccess', board.hasRestrictedAccess, true);
    formik.setFieldValue(
      'cardsHaveCyrillicTitles',
      board.cardsHaveCyrillicTitles ?? true,
      true,
    );
  }, [board]);

  useEffect(() => {
    accountGetListFX({
      filter: {
        status: 'ACTIVE',
      },
    })
    return formik.resetForm
  }, []);

  const btnRef = React.useRef<HTMLButtonElement>(null);
  const copyBtnRef = React.useRef<HTMLElement|null>(null);

  const [openMenuMembers, setOpenMenuMembers] = useState(false);


  const onOpenMenuMembers =()=> {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'fixed';
      tempDiv.style.top = `0px`;
      tempDiv.style.left = `${rect.left}px`;
      tempDiv.style.width = `${rect.width}px`;
      tempDiv.style.height = `${rect.height}px`;
      document.body.appendChild(tempDiv);
      copyBtnRef.current = tempDiv;
    }
    setOpenMenuMembers(true);
  }
  const onCloseMenuMembers = () => {
    setOpenMenuMembers(false);
    if (copyBtnRef.current) {
      document.body.removeChild(copyBtnRef.current);
      copyBtnRef.current = null;
    }
  };

  const onClickMember = (member: Account, isChecked: boolean) => {

    if (isChecked) {
      formik.setFieldValue('allowedAccounts', [...(formik.values.allowedAccounts??[]).filter(item=>item.uuid!==member.uuid)],true)
      return;
    }
    formik.setFieldValue('allowedAccounts', [...(formik.values.allowedAccounts??[]),member],true)
  };


const btnDisabled = Boolean(Object.values(formik.errors).length) || accountGetListFXPending

  return (
    <>
      <TitleItem
        title="Настройки доступа"
        onBack={() => setMenuView('Default')}
      />

      <List>
        <ListItem sx={{ justifyContent: 'space-between' }}>
          <Box component="form" onSubmit={formik.handleSubmit} noValidate mt={1}>
            <FormGroup>
              <FormControlLabel
                control={<Checkbox
                  checked={formik.values.hasRestrictedAccess}
                  onChange={formik.handleChange}
                  name="hasRestrictedAccess"
                />}
                label="Сделать доску приватной"
                disabled={accountGetListFXPending}
              />
              {formik.values.hasRestrictedAccess &&  <Members title="Доступно для:" members={formik.values.allowedAccounts??[]} onAdd={onOpenMenuMembers} btnRef={btnRef}/>}

              {!accountGetListFXPending && <Typography variant="body2" color="red" fontWeight={400}>
                {formik.errors.allowedAccounts}
              </Typography>}
              <MenuMembers
                openMenu={openMenuMembers}
                anchorEl={copyBtnRef?.current}
                handleClose={onCloseMenuMembers}
                accounts={accountsForAces??[]}
                listChecked={formik.values.allowedAccounts?.map(item=>item.uuid) || []}
                onClickMember={onClickMember}
                headerTitle="Доступно для"
                titleList=""
                onClearAll={
                  () => {
                    formik.setFieldValue('allowedAccounts', [], true);
                  }
                }
                onSelectAll={
                  () => {
                    formik.setFieldValue('allowedAccounts', accountsForAces?.map(({uuid,email})=>({uuid,email})), true);
                  }
                }
              />
            </FormGroup>
            <FormGroup sx={{marginTop: 2}}>
              <Button onClick={formik.submitForm} disabled={btnDisabled}>Сохранить</Button>

            </FormGroup>
          </Box>


        </ListItem>
      </List>
      <Divider />

    </>
  );
}